import { type FC, useEffect, useState } from 'react';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import { Clipboard, EyeFill } from 'react-bootstrap-icons';
import { useAuth, useNotifications, useQueryRequest } from '../../../modules/hooks';
import { endpoints } from '../../../modules/mappers/urls';

interface UserDetailsModalProps {
  show: boolean;
  handleClose: () => void;
  temporaryAccessCodeId: string;
}

export const ViewTemporaryAccessCodeModal: FC<UserDetailsModalProps> = ({
  show,
  handleClose,
  temporaryAccessCodeId,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const [showPassword, setShowPassword] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const { credentialsInfo } = useAuth()!;

  const lockUrl = `${endpoints.LOCKS}/temporary-access-code/${temporaryAccessCodeId}`;
  const {
    data: locksResult,
    loading: locksLoading,
    error: locksError,
  } = useQueryRequest<string>(lockUrl, {
    authToken: credentialsInfo?.token,
  });

  const copyToClipboardHandler = () => {
    navigator.clipboard.writeText(accessCode);
    setSimpleToasts({
      type: 'success',
      message: 'Access code copied successfully',
      show: true,
    });
  };

  useEffect(() => {
    if (locksResult) {
      const currentAccessCode = `${locksResult.result}#`;
      setAccessCode(currentAccessCode);
    }

    if (locksError) {
      setSimpleToasts({
        type: 'danger',
        message: locksError?.message || 'Failed to load access code. Please try again.',
        show: true,
      });
    }
  }, [locksResult, locksError]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="post-editor-modal modal-200w"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title className="editor-title">Access Code</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {locksLoading ? (
          <Container style={{ display: 'flex', justifyContent: 'center' }}>
            <Spinner
              animation="border"
              variant="primary"
              style={{ color: '#2BD579', marginRight: '10px' }}
            />
          </Container>
        ) : (
          <Form>
            <Row>
              <Col xs={12}>
                <Form.Label className="editor-title-label"> Your Access Code</Form.Label>
              </Col>
              <Col xs={10}>
                <Form.Group>
                  <Form.Control
                    readOnly
                    type={showPassword ? 'text ' : 'password'}
                    value={accessCode}
                  />
                </Form.Group>
              </Col>
              <Col xs={2}>
                <Button>
                  <EyeFill onClick={() => setShowPassword(!showPassword)} size={18} />
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer
        style={{
          display: 'flex',
          justifyContent: 'end',
          marginTop: '24px',
        }}
      >
        <Button className="cancel-button" onClick={handleClose}>
          Cancel
        </Button>
        <Button className="save-button" onClick={() => copyToClipboardHandler()}>
          <Clipboard />
          Copy
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
